import React from "react";
import { Warning, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "./ui/button";

/**
 * App-wide error boundary. Catches render crashes in any child component
 * and shows a recoverable error screen instead of a blank page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || String(this.state.error || "Unknown error");
    const stack = this.state.errorInfo?.componentStack || "";

    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="error-boundary">
        <div className="max-w-lg w-full bg-white border border-black/10 p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Warning size={20} weight="bold" className="text-amber-600" />
            </div>
            <div>
              <h1 className="text-lg font-black text-[#0A0A0A]">Something went wrong</h1>
              <p className="text-sm text-[#525252] mt-1">
                We hit an error while rendering this page. You can refresh or head back to the dashboard.
              </p>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            <Button
              onClick={this.handleReload}
              className="bg-[#9FFF00] hover:bg-[#8AE600] text-black rounded-none font-bold"
              data-testid="error-reload-btn"
            >
              <ArrowClockwise size={14} className="mr-1" /> Refresh
            </Button>
            <Button
              onClick={this.handleGoHome}
              variant="outline"
              className="border-black/20 rounded-none font-bold"
              data-testid="error-home-btn"
            >
              Back to Dashboard
            </Button>
          </div>

          <details className="text-xs text-[#525252] bg-zinc-50 p-3 border border-black/5">
            <summary className="cursor-pointer font-bold uppercase tracking-wider">Technical details</summary>
            <p className="mt-2 font-mono break-words">{msg}</p>
            {stack && (
              <pre className="mt-2 overflow-x-auto text-[10px] text-[#737373]">{stack.split("\n").slice(0, 6).join("\n")}</pre>
            )}
          </details>
        </div>
      </div>
    );
  }
}
